import { Link } from 'react-router-dom';
import { CreditCard, ArrowLeft } from 'lucide-react';

const LegalLayout = ({ title, children }) => (
  <div className="min-h-screen bg-[#020617]">
    <header className="glass-heavy border-b border-white/5">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">MeTag</span>
          </Link>
          <Link to="/" className="text-slate-400 hover:text-white flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </header>

    <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl font-bold text-white mb-8">{title}</h1>
      <div className="prose prose-invert max-w-none">
        {children}
      </div>
    </main>

    <footer className="border-t border-slate-800 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-slate-500 text-sm">
        © {new Date().getFullYear()} MeTag.cc. All rights reserved.
      </div>
    </footer>
  </div>
);

export default function ReturnPolicy() {
  return (
    <LegalLayout title="Cancellations & Refunds Policy">
      <div className="space-y-6 text-slate-300" data-testid="return-policy-page">
        <p className="text-slate-400">Last updated: December 2024</p>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">No Returns Policy</h2>
          <p>Due to the personalized nature of our NFC cards, we do not accept returns or provide refunds for change of mind.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Order Cancellation</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Orders can only be cancelled before they are shipped</li>
            <li>To cancel an order, contact us immediately at support@metag.com</li>
            <li>Once shipped, orders cannot be cancelled</li>
            <li>Refunds for cancelled orders will be processed within 5-7 business days</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Defective Products</h2>
          <p>If you receive a defective MeTag card:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Contact us within 7 days of delivery</li>
            <li>Provide your order number and photos/videos of the defect</li>
            <li>We will replace the card at no additional cost</li>
            <li>Defective cards must be returned (shipping covered by us)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Non-Delivery</h2>
          <p>If your order is not delivered within the expected timeframe:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Contact us with your order number</li>
            <li>We will investigate with the shipping carrier</li>
            <li>If the package is confirmed lost, we will either refund or reship</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">How to Request a Refund</h2>
          <p>To request a refund (for eligible cases):</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Email us at support@metag.com</li>
            <li>Include your order number</li>
            <li>Describe the issue in detail</li>
            <li>Attach relevant photos if applicable</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Refund Processing</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Approved refunds are processed to the original payment method</li>
            <li>Refund processing takes 5-7 business days</li>
            <li>Bank processing may take additional 3-5 business days</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Contact Us</h2>
          <p>For any questions about our refund policy:</p>
          <ul className="mt-2 space-y-1">
            <li>Email: support@metag.com</li>
            <li>Phone: +91 78376 40500</li>
          </ul>
        </section>
      </div>
    </LegalLayout>
  );
}
